import { ID, FirebaseResponse, Checklist as DBChecklist } from "src/types/index";

import { defaultChecklistsObj } from "./constants";
import {
  getDefaultChecklists as FBGetDefaultChecklists,
  getDefaultSublocationChecklists as FBGetDefaultSublocationChecklists,
  getCustomSublocationChecklists as FBGetCustomSublocationChecklists,
  getChecklistsByLocationAndSublocation as FBGetChecklistsByLocationAndSublocation,
  getChecklistsByLocation as FBGetChecklistsByLocation,
  getChecklistsByClient as FBGetChecklistsByClient,
} from "src/services/Firebase/checklists";

type InputRules = {
  required: boolean;
  pattern: string;
};

type Input = {
  placeholder: string;
  errorMessage: string;
  rules: InputRules;
};

type RadioItem = {
  label: string;
  value: string;
};

type Question = {
  input?: Input;
  radio?: RadioItem[];
};

type ChecklistQuestion = {
  label: string;
  questions: Question[];
};

type Checklist = {
  questions: ChecklistQuestion[];
};

type ChecklistsObj = {
  [key: string]: Checklist;
};

type ChecklistsParams = {
  clientId: ID;
  sublocationType?: ID;
  isSublocationTypeDefault?: boolean;
  periodId: ID;
  locationId: ID;
};

type ChecklistsWithSublocationParams = ChecklistsParams & {
  reportType: string;
};

// export for tests
export const getActiveChecklists = (data: Record<string, DBChecklist>) =>
  Object.keys(data).reduce((acc, key) => {
    if (data[key].active) {
      acc = { ...acc, [key]: data[key] };
    }

    return acc;
  }, {});

const getChecklistsIfSublocationNotSet = async ({
  clientId,
  sublocationType,
  locationId,
  reportType,
  periodId,
}: ChecklistsWithSublocationParams) => {
  let response;
  try {
    response = await FBGetChecklistsByLocation({
      locationId,
      sublocationTypeId: sublocationType as ID,
      reportType,
      periodId,
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    console.log("Unable to retrieve checklists by location");
  }

  if (!response || !response.success) {
    try {
      response = await FBGetChecklistsByClient({
        sublocationTypeId: sublocationType as ID,
        clientId,
        reportType,
        periodId,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      console.log("Unable to retrieve checklists by client");
    }
  }

  if (!response || !response.success) {
    try {
      response = await FBGetDefaultChecklists(reportType);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      console.log("Unable to retrieve default checklists");
    }
  }

  if (!response || !response.success) {
    try {
      // eslint-disable-next-line no-unused-expressions
      response;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return {
        success: false,
        error: "Unable to retrieve checklists",
      };
    }
  }

  if (!response || !response.success) {
    return {
      success: false,
      error: response && "error" in response ? (response as any).error : "Unable to retrieve checklists",
    };
  }

  const { success, data } = response;

  return { success, data: getActiveChecklists(data) };
};

const getChecklistsIfSublocationIsSet = async ({
  clientId,
  sublocationType,
  locationId,
  reportType,
  periodId,
}: ChecklistsWithSublocationParams) => {
  let response;
  try {
    response = await FBGetChecklistsByLocationAndSublocation({
      locationId,
      sublocationTypeId: sublocationType as ID,
      reportType,
      periodId,
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    console.log("Unable to retrieve checklists by location and sub-location");
  }

  if (!response || !response.success) {
    try {
      response = await FBGetCustomSublocationChecklists({
        sublocationTypeId: sublocationType as ID,
        clientId,
        reportType,
        periodId,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      console.log("Unable to retrieve checklists by client and sub-location");
    }
  }

  if (!response || !response.success) {
    try {
      response = await FBGetDefaultSublocationChecklists({
        sublocationTypeId: sublocationType as ID,
        reportType,
        periodId,
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return {
        success: false,
        error: "Unable to retrieve checklists",
      };
    }
  }

  if (!response || !response.success) {
    return {
      success: false,
      error: response && "error" in response ? (response as any).error : "Unable to retrieve checklists",
    };
  }

  const { success, data } = response;

  return { success, data: getActiveChecklists(data) };
};

const getChecklists = (reportType: string) => async (params: ChecklistsParams) => {
  if (params.sublocationType) {
    return getChecklistsIfSublocationIsSet({ ...params, reportType });
  }

  return getChecklistsIfSublocationNotSet({ ...params, reportType });
};

export const getClockInChecklists: (params: ChecklistsParams) => Promise<FirebaseResponse<ChecklistsObj>> =
  getChecklists("clockIn");

export const getClockOutChecklists: (params: ChecklistsParams) => Promise<FirebaseResponse<ChecklistsObj>> =
  getChecklists("clockOut");

export const getChecklistReport = ({ checklistsData, answers, checklistInfo }: any) => ({
  label: checklistInfo.label,
  default: !!checklistInfo.default,
  oneClick: !!checklistInfo.oneClick,
  includeInEmailReport: !!checklistInfo.includeInEmailReport,
  answers: answers.map((sectionAnswers: any, sectionIndex: number) => {
    const sectionQuestion = checklistsData.questions[checklistInfo.startIndex + sectionIndex];
    return {
      label: sectionQuestion.label,
      questions: sectionAnswers.flatMap((answer: any, questionIndex: number) => {
        const question = sectionQuestion.questions[questionIndex];
        let label;
        let value;
        let passAnswer;
        if (checklistInfo.oneClick) {
          return question.questions.map(({ label, defaultValue, passAnswer }: any) => ({
            label,
            value: defaultValue,
            passAnswer,
          }));
        } else {
          if (question.toggle) {
            label = question.toggle.label;
            value = !!answer;
          } else {
            label = question.label;
            if (question.radio) {
              value = question.radio[answer]?.label || "";
            } else {
              value = answer;
            }
          }
          passAnswer = question.passAnswer;
        }
        return { label, value, passAnswer };
      }),
    };
  }),
});

export const getReportChecklistsObject = ({ checklistsData, answers }: any): any =>
  checklistsData.checklistsInfo.reduce((acc: any, checklistInfo: any, idx: number) => {
    return {
      ...acc,
      [idx]: getChecklistReport({
        checklistsData,
        checklistInfo,
        answers: answers.slice(checklistInfo.startIndex, checklistInfo.endIndex),
      }),
    };
  }, {});

export const reduceChecklists = (checklistsObj: any, isDefault = true) => {
  return Object.keys(checklistsObj)
    .sort((key1, key2) => checklistsObj[key2].priority - checklistsObj[key1].priority)
    .reduce((acc: any, checklistKey) => {
      const checklist = checklistsObj[checklistKey];
      const startIndex = acc.questions.length;
      const questions = [...acc.questions];
      let endIndex;
      if (!checklist.oneClick) {
        endIndex = startIndex + checklist.questions.length;
        questions.push(...checklist.questions);
      } else {
        endIndex = startIndex + 1;
        questions.push({
          questions: [
            {
              labels: checklist.questions.map((question: any) => question.label),
              questions: checklist.questions,
              checkbox: {
                label: checklist.answerInOneClickLabel,
                rules: {
                  required: true,
                },
              },
            },
          ],
        });
      }
      const checklistsInfo = [
        ...acc.checklistsInfo,
        {
          id: checklistKey,
          default: isDefault,
          label: checklist.label,
          oneClick: checklist.oneClick,
          includeInEmailReport: checklist.includeInEmailReport,
          startIndex,
          endIndex,
        },
      ];

      return { checklistsInfo, questions };
    }, defaultChecklistsObj);
};
